import {useConfig} from '@aboutyou/config';
import {
  getGrpcErrorDetails,
  useLegacyGrpcRequestCallback,
} from '@aboutyou/data-fetcher';
import {PaymentsService_createGiftCardRefundLazy} from '@aboutyou/grpc-clients/grips/protos/payments/v1/payments_service.grpc.lazy';
import {
  Device,
  RequestConfig,
} from '@aboutyou/grpc-clients/grips/protos/shared/shared.types';
import {useTranslation} from '@aboutyou/i18n';
import {MaybeTokenOrSessionId, useUserTokenOrSessionId} from '@aboutyou/user';
import {useForm} from 'react-hook-form';
import {getGrpcFieldViolationsAsFormError} from './getGrpcFieldViolationsAsFormError';

export enum GiftCardType {
  SecretNumber = '1',
  SerialNumber = '2',
}

interface GiftCardRefundFormItems {
  type: GiftCardType;
  giftCardNumber: string;
  firstName: string;
  lastName: string;
  street: string;
  postalCode: string;
  city: string;
  country: string;
  email: string;
  iban: string;
  secretNumber: string;
  serialNumber: string;
}

type IbanError =
  | 'STRING_TOO_LONG'
  | 'STRING_TOO_SHORT'
  | 'INVALID_FORMAT'
  | 'IBAN_COUNTRY_MISMATCH';

const useGetTranslationForIbanError = () => {
  const {t} = useTranslation();

  return (errorCode: IbanError) => {
    switch (errorCode) {
      case 'STRING_TOO_LONG':
        return t('The input is too long');
      case 'STRING_TOO_SHORT':
        return t('The input is too short');
      case 'INVALID_FORMAT':
        return t('The IBAN entered is invalid');
      case 'IBAN_COUNTRY_MISMATCH':
        return t('The IBAN you have entered does not match your country');
      default:
        return t('The IBAN entered is invalid');
    }
  };
};

interface GiftCardErrorDetail {
  fieldViolations: {
    field: string;
    description: string;
  }[];
}

/**
 * We need this function in order to construct a legacy session object
 * which can be consumed by grips
 * @param tokenOrSessionId the tadarida token or session id
 * @returns a legacy session object
 */
const getSession = (
  tokenOrSessionId: MaybeTokenOrSessionId,
): RequestConfig['session'] => {
  if (!tokenOrSessionId) {
    return;
  }

  return tokenOrSessionId && 'token' in tokenOrSessionId
    ? {
        $case: 'customerSession' as const,
        customerSession: {authToken: tokenOrSessionId.token},
      }
    : {
        $case: 'guestSession' as const,
        guestSession: {sessionId: (tokenOrSessionId as any)?.sessionId},
      };
};

export const useGiftCardReturnForm = () => {
  const {data: tokenOrSessionId} = useUserTokenOrSessionId();
  const getIbanError = useGetTranslationForIbanError();
  const {
    handleSubmit,
    register,
    formState: {errors},
    reset,
    setError,
    setValue,
    watch,
    control,
  } = useForm<GiftCardRefundFormItems>({
    defaultValues: {
      type: GiftCardType.SecretNumber,
    },
    mode: 'onChange',
  });

  const {mutateAsync, isSuccess, isPending} = useLegacyGrpcRequestCallback(
    PaymentsService_createGiftCardRefundLazy,
    {
      onSuccess: () => {
        reset();
        window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
      },
    },
  );
  const {device, legacyConfig} = useConfig();

  const createGiftCardRefund = async (items: GiftCardRefundFormItems) => {
    mutateAsync({
      config: {
        device: device === 'desktop' ? Device.DESKTOP : Device.MOBILE_WEB,
        childShopId: legacyConfig.childShopId,
        shopId: legacyConfig.shopId,
        session: getSession(tokenOrSessionId),
      },
      code: items.giftCardNumber,
      firstName: items.firstName,
      lastName: items.lastName,
      street: items.street,
      postalCode: items.postalCode,
      city: items.city,
      country: items.country,
      email: items.email,
      iban: items.iban,
      type: Number(items.type),
    }).catch(error => {
      const errorDetails = getGrpcErrorDetails(error);
      const fieldErrors = errorDetails
        ? getGrpcFieldViolationsAsFormError(errorDetails, ['iban'])
        : [];

      if (fieldErrors) {
        const {fieldViolations} = errorDetails![0] as GiftCardErrorDetail;

        const ibanFieldViolation = fieldViolations.find(
          (violation: {field: string; description: string}) =>
            violation.field === 'iban',
        );
        setError('iban', {
          message: getIbanError(ibanFieldViolation!.description as IbanError),
        });
      }
    });
  };

  return {
    handleSubmit,
    register,
    errors,
    isSuccess,
    createGiftCardRefund,
    watch,
    control,
    setValue,
    isLoading: isPending,
  };
};
